import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useAppName, useBaseUrl, usePostData } from "../../utils/utils";
import Swal from "sweetalert2";
import '../../styles/platform-colors.css';

// Add new types for clarity
interface StaffTitle {
  id: number;
  name: string;
}

interface County {
  county_code: number;
  county: string;
}

interface Town {
  town_id: number;
  town: string;
}

interface Category {
  id: number;
  name: string;
}

interface Service {
  service_id: number;
  minimum_rate: number;
  hourly_rate: number;
  is_billable: boolean;
}

interface User {
  fname: string;
  lname: string;
  mname?: string;
  email: string;
  password: string;
  phone?: string;
  avatar?: string;
  title_id: number;
  town?: number;
}

interface Facility {
  name: string;
  county?: number;
  town: number;
  category: number;
  description?: string;
  postcode?: number;
  capacity?: number;
  logo?: string;
  email: string;
  username?: string;
  password: string;
  phone?: string;
  website?: string;
  designation?: number;
  services: Service[];
  users: User[];
}

interface Institution {
  name: string;
  website: string;
  town: number;
  postcode?: number;
  logo?: string;
  email: string;
  username?: string;
  password: string;
}

interface SearchableOption {
  value: string;
  label: string;
}

interface PasswordFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  visible: boolean;
  onToggle: () => void;
  inputClassName: string;
  className?: string;
}

const PasswordField: React.FC<PasswordFieldProps> = ({
  value,
  onChange,
  placeholder,
  visible,
  onToggle,
  inputClassName,
  className,
}) => (
  <div className="relative">
    <input
      className={`${className || inputClassName} pr-12`}
      placeholder={placeholder}
      type={visible ? 'text' : 'password'}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
    <button
      type="button"
      className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-400 hover:text-teal-600 transition-colors"
      onClick={onToggle}
      aria-label={visible ? 'Hide password' : 'Show password'}
    >
      {visible ? (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.956 9.956 0 012.223-3.592m3.1-2.398A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a9.97 9.97 0 01-4.293 5.248M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 9L3 3" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.522 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.478 0-8.268-2.943-9.542-7z" />
        </svg>
      )}
    </button>
  </div>
);

interface SearchableSelectProps {
  options: SearchableOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  inputClassName: string;
  disabled?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  inputClassName,
  disabled = false,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const selectedLabel = options.find(option => option.value === value)?.label ?? '';
  const filteredOptions = options.filter(option => option.label.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    if (!isEditing) {
      setQuery(selectedLabel);
    }
  }, [isEditing, selectedLabel]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsEditing(false);
        if (value) {
          setQuery(selectedLabel);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectedLabel, value]);

  return (
    <div className="relative" ref={wrapperRef}>
      <input
        className={`${inputClassName} pr-12`}
        placeholder={disabled ? placeholder : searchPlaceholder}
        value={query}
        disabled={disabled}
        onFocus={() => {
          if (!disabled) {
            setIsOpen(true);
            setIsEditing(true);
          }
        }}
        onChange={e => {
          const nextValue = e.target.value;
          setQuery(nextValue);
          setIsOpen(true);
          setIsEditing(true);
          if (value) {
            onChange('');
          }
        }}
      />
      <button
        type="button"
        className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-400 hover:text-teal-600 transition-colors"
        onClick={() => {
          if (!disabled) {
            setIsOpen(current => !current);
            setIsEditing(true);
          }
        }}
        aria-label={`Toggle ${placeholder.toLowerCase()} options`}
      >
        <svg className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && !disabled && (
        <div className="absolute z-30 mt-2 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-xl">
          {filteredOptions.length > 0 ? (
            filteredOptions.map(option => (
              <button
                key={option.value}
                type="button"
                className={`w-full px-4 py-3 text-left text-sm transition-colors ${option.value === value ? 'bg-teal-50 text-teal-700' : 'text-gray-700 hover:bg-gray-50'}`}
                onMouseDown={e => e.preventDefault()}
                onClick={() => {
                  onChange(option.value);
                  setQuery(option.label);
                  setIsOpen(false);
                  setIsEditing(false);
                }}
              >
                {option.label}
              </button>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-gray-400">{emptyText}</div>
          )}
        </div>
      )}
    </div>
  );
};

const NewRegister: React.FC = () => {
    const navigate = useNavigate();
    useEffect(() => {
        document.title = "Registration - Uzima Health Care";
        GetCounties();
        GetCategories();
        fetchServices();
    }, []);

    const [mainStep, setMainStep] = useState<'select' | 'patient' | 'partner' | 'home'>('select');
    const [step, setStep] = useState(0);
    const [subStep, setSubStep] = useState(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fName, setFName] = useState<string>("");
    const [lName, setLName] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [counties, setCounties] = useState<County[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [selectedCounty, setSelectedCounty] = useState<string>("");
    const [selectedTown, setSelectedTown] = useState<string>("");
    const [towns, setTowns] = useState<Town[]>([]);
    const [tenb, setTenb] = useState<boolean>(false);
    const [facilityCounties, setFacilityCounties] = useState<Record<number, string>>({});
    const [facilityTowns, setFacilityTowns] = useState<Record<number, Town[]>>({});
    const [facility, setFacility] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<any>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [cPass, setCPass] = useState<string>("");
    const [lastTest, setLastTest] = useState("a user");
    const [services, setServices] = useState<any[]>([]);
    const [selectedInterests, setSelectedInterests] = useState<any[]>([]);
    const [consent, setConsent] = useState(false);
    const base_url = useBaseUrl();
    const app_name = useAppName();
    const [staffTitles, setStaffTitles] = useState<StaffTitle[]>([]);
    const [institution, setInstitution] = useState<Partial<Institution>>({});
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [institutionName, setInstitutionName] = useState<string>("Institution Name");
    const [facilityName, setFacilityName] = useState<string>("Facility Name");
    const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});

    const requiredInstitutionFields = ["name", "website", "town", "email", "username", "password"];
    const isInstitutionValid = (inst: Partial<Institution>) => requiredInstitutionFields.every(f => !!inst[f as keyof Institution]);
    const isFacilityValid = (f: Facility) => f.name && f.town && f.category && f.email && f.password && f.users.length > 0 && f.users.every(u => u.fname && u.lname && u.email && u.password && u.title_id);
    const canGoToFacilities = isInstitutionValid(institution);
    const canGoToReview = facilities.length > 0 && facilities.every(isFacilityValid);
    const countyOptions = counties.map((county: County) => ({ value: String(county.county_code), label: county.county }));
    const townOptions = towns.map((town: Town) => ({ value: String(town.town_id), label: town.town }));

    const GetCounties = async () => {
        const url = `${base_url}/facilities/get-counties`
        const formaData = new FormData()
        formaData.append("selector", "9999")
        const resp = await usePostData(url, formaData)
        if (resp !== undefined){ setCounties(resp) } else { setCounties([]) }
    }
    const GetTowns = async (countyId: string) => {
        let url = `${base_url}/facilities/get-post-towns`
        const formData = new FormData()
        formData.append("county_id", countyId)
        const resp = await usePostData(url, formData)
        if(resp !== undefined){ return resp }
        return []
    }
    const GetCategories = async () => {
        let url = `${base_url}/facilities/categories`
        const formData = new FormData()
        const resp = await usePostData(url, formData)
        if (resp !== undefined){ setCategories(resp) }
    }
    const fetchServices = async () => {
        const url = `${base_url}/facilities/service-list`;
        try {
            const response = await fetch(url, { method: 'GET' });
            const data = await response.json();
            if (data.status === 'success' && Array.isArray(data.data)) {
                setServices(data.data);
            }
        } catch (e) {
            setServices([]);
        }
    };
    const RegisterUser = async () => {
        let url = `${base_url}/facilities/newreg`
        const payload = {
            users: [
                {
                    fname: firstName,
                    lname: lastName,
                    email: email,
                    password: password,
                    town: parseInt(selectedTown)
                }
            ],
            services: selectedInterests.map(id => ({
                service_id: id
            }))
        };

        try {
            const resp = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await resp.json();
            if (data.status === 'success') {
                await Swal.fire({ 
                    icon: "success", 
                    title: "Registration Successful", 
                    text: `Dear ${payload.users[0].fname}, your registration was successful, you are now an Uzima Patient. You will now be redirected to the login page, log in as a patient to complete the process.`,
                    confirmButtonText: 'OK'
                });
                navigate('/institution/newlog');
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: 'Registration Failed',
                    text: data.message || 'An error occurred during registration. Please try again.'
                });
            }
        } catch (error) {
            await Swal.fire({
                icon: 'error',
                title: 'Registration Failed',
                text: 'An error occurred during registration. Please try again.'
            });
        }
    }
    useEffect(()=>{
        const loadTowns = async () => {
            if (selectedCounty && parseInt(selectedCounty) !== 999 && selectedCounty !== undefined) {
                const nextTowns = await GetTowns(selectedCounty);
                setTowns(nextTowns);
                setTenb(true);
            } else {
                setTowns([]);
                setTenb(false);
            }
        }
        loadTowns();
    }, [selectedCounty])

    useEffect(() => {
        fetch(`${base_url}/facilities/staff-titles`)
          .then(res => res.json())
          .then(data => {
            if (data.status === 'success') {
              setStaffTitles(data.data.map((t: any) => ({ id: t.id, name: t.name })));
            }
          });
    }, []);

    const addFacility = (facility: Facility) => {
        setFacilities([...facilities, facility]);
    };

    const addUserToFacility = (facilityIdx: number, user: User) => {
        const updated = [...facilities];
        updated[facilityIdx].users.push(user);
        setFacilities(updated);
    };

    const addServiceToFacility = (facilityIdx: number, service: Service) => {
        const updated = [...facilities];
        updated[facilityIdx].services.push(service);
        setFacilities(updated);
    };

    const RegisterPartner = async () => {
        const payload = {
            institution,
            facilities
        };
        const resp = await fetch(`${base_url}/facilities/newreg`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        const data = await resp.json();
        if (data.status === 'success') {
            await Swal.fire({
                icon: 'success',
                title: 'Registration Successful',
                text: `Dear ${institution.name}, your registration was successful, you are now an Uzima Partner. You will now be redirected to the login page, log in as a partner to complete the process.`,
                confirmButtonText: 'OK'
            });
            navigate('/institution/newlog');
        } else {
            await Swal.fire({
                icon: 'error',
                title: 'Registration Failed',
                text: data.message || 'An error occurred during registration. Please try again.'
            });
        }
    };

    const updateFacility = (idx: number, updated: Partial<Facility>) => {
        setFacilities(facilities => facilities.map((f, i) => i === idx ? { ...f, ...updated } : f));
    };
    const updateUser = (facilityIdx: number, userIdx: number, updated: Partial<User>) => {
        setFacilities(facilities => facilities.map((f, i) => i === facilityIdx ? {
            ...f,
            users: f.users.map((u, j) => j === userIdx ? { ...u, ...updated } : u)
        } : f));
    };
    const updateService = (facilityIdx: number, serviceIdx: number, updated: Partial<Service>) => {
        setFacilities(facilities => facilities.map((f, i) => i === facilityIdx ? {
            ...f,
            services: f.services.map((s, j) => j === serviceIdx ? { ...s, ...updated } : s)
        } : f));
    };
    const togglePasswordVisibility = (field: string) => {
        setVisiblePasswords(current => ({ ...current, [field]: !current[field] }));
    };
    const handleCountySelect = (value: string, onSelected?: () => void) => {
        setSelectedCounty(value);
        setSelectedTown("");
        setTowns([]);
        if (onSelected) {
            onSelected();
        }
    };
    const handleFacilityCountySelect = async (facilityIdx: number, value: string, users: User[]) => {
        setFacilityCounties(current => ({ ...current, [facilityIdx]: value }));
        setFacilityTowns(current => ({ ...current, [facilityIdx]: [] }));
        updateFacility(facilityIdx, {
            county: value ? Number(value) : undefined,
            town: 0,
            users: users.map(user => ({ ...user, town: undefined }))
        });
        if (value) {
            const nextTowns = await GetTowns(value);
            setFacilityTowns(current => ({ ...current, [facilityIdx]: nextTowns }));
        }
    };
    const startPatientRegistration = () => {
        setMainStep('patient');
        setSubStep(0);
        setSelectedCounty('');
        setSelectedTown('');
        setTowns([]);
    };
    const startPartnerRegistration = () => {
        setMainStep('partner');
        setStep(0);
        setSelectedCounty('');
        setSelectedTown('');
        setTowns([]);
        setFacilityCounties({});
        setFacilityTowns({});
    };

    // ─── SHARED UI HELPERS ───────────────────────────────────
    const inputClass = "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all duration-200";
    const selectClass = "w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-all duration-200 appearance-none";
    const labelClass = "block text-sm font-semibold text-gray-700 mb-1.5";
    const btnPrimary = "px-5 sm:px-8 py-2.5 sm:py-3 bg-teal-600 text-white rounded-xl font-semibold text-xs sm:text-sm hover:bg-teal-700 active:bg-teal-800 transition-all duration-200 shadow-lg shadow-teal-600/20 hover:shadow-teal-600/30 flex items-center gap-2";
    const btnOutline = "px-5 sm:px-8 py-2.5 sm:py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-semibold text-xs sm:text-sm hover:border-gray-300 hover:bg-gray-50 transition-all duration-200 flex items-center gap-2";
    const btnDisabled = "px-5 sm:px-8 py-2.5 sm:py-3 bg-gray-200 text-gray-400 rounded-xl font-semibold text-xs sm:text-sm cursor-not-allowed";

    const StepIndicator = ({ steps, current }: { steps: string[], current: number }) => (
        <div className="flex items-center justify-center mb-6 sm:mb-10">
            {steps.map((label, i) => (
                <React.Fragment key={i}>
                    <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all duration-300 ${
                            i < current ? 'bg-teal-600 text-white' :
                            i === current ? 'bg-teal-600 text-white ring-4 ring-teal-100' :
                            'bg-gray-100 text-gray-400'
                        }`}>
                            {i < current ? (
                                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                            ) : i + 1}
                        </div>
                        <span className={`text-[10px] sm:text-xs mt-1.5 sm:mt-2 font-medium ${i <= current ? 'text-teal-700' : 'text-gray-400'}`}>{label}</span>
                    </div>
                    {i < steps.length - 1 && (
                        <div className={`w-8 sm:w-16 h-0.5 mx-1 sm:mx-2 mb-5 transition-all duration-300 ${i < current ? 'bg-teal-600' : 'bg-gray-200'}`} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );

    // ─── RENDER ──────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-teal-50 flex">
            {/* Left Branded Panel */}
            <div className="hidden lg:flex lg:w-[480px] xl:w-[520px] flex-col relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
                {/* Decorative circles */}
                <div className="absolute -top-20 -left-20 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-20 -right-20 w-80 h-80 bg-teal-400/10 rounded-full blur-3xl" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-teal-500/5 rounded-full blur-2xl" />

                <div className="relative z-10 flex flex-col h-full p-10">
                    {/* Logo */}
                    <div className="flex items-center gap-3 mb-16">
                        <div className="w-10 h-10 bg-teal-500 rounded-xl flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </div>
                        <span className="text-white font-bold text-xl tracking-tight">Uzima Healthcare</span>
                    </div>

                    {/* Hero Content */}
                    <div className="flex-1 flex flex-col justify-center">
                        <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
                            Healthcare,<br />
                            <span className="text-teal-400">reimagined.</span>
                        </h1>
                        <p className="text-slate-300 text-lg leading-relaxed mb-10">
                            Join Kenya's most trusted healthcare collaboration platform. Connect with top providers, manage your health records, and take control of your wellness journey.
                        </p>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                                <div className="text-2xl font-bold text-teal-400">47</div>
                                <div className="text-xs text-slate-400 mt-1">Counties</div>
                            </div>
                            <div className="text-center p-4 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                                <div className="text-2xl font-bold text-teal-400">200+</div>
                                <div className="text-xs text-slate-400 mt-1">Facilities</div>
                            </div>
                            <div className="text-center p-4 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-sm">
                                <div className="text-2xl font-bold text-teal-400">50+</div>
                                <div className="text-xs text-slate-400 mt-1">Services</div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="text-slate-500 text-xs">
                        &copy; {new Date().getFullYear()} Uzima Healthcare. All rights reserved.
                    </div>
                </div>
            </div>

            {/* Right Content Panel */}
            <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-6 sm:py-10 overflow-y-auto">
                <div className="w-full max-w-2xl">
                    {/* Mobile Logo */}
                    <div className="flex lg:hidden items-center justify-center gap-3 mb-10">
                        <div className="w-10 h-10 bg-teal-600 rounded-xl flex items-center justify-center">
                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                            </svg>
                        </div>
                        <span className="text-slate-800 font-bold text-xl tracking-tight">Uzima Healthcare</span>
                    </div>

                    {/* ═══════════ ROLE SELECTION ═══════════ */}
                    {mainStep === 'select' && (
                        <div className="animate-fadeIn">
                            <div className="text-center mb-12">
                                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Get Started</h1>
                                <p className="text-gray-500 text-base">Choose how you'd like to join Uzima Healthcare</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                                {/* Patient Card */}
                                <button
                                    onClick={startPatientRegistration}
                                    className="group relative bg-white border-2 border-gray-100 rounded-2xl p-8 text-left hover:border-teal-200 hover:shadow-xl hover:shadow-teal-500/5 transition-all duration-300"
                                >
                                    <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                    <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-teal-500/20">
                                        <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">I'm a Patient</h3>
                                    <p className="text-sm text-gray-500 leading-relaxed">Create your personal health account to access medical services, manage records, and connect with providers.</p>
                                </button>

                                {/* Partner Card */}
                                <button
                                    onClick={startPartnerRegistration}
                                    className="group relative bg-white border-2 border-gray-100 rounded-2xl p-8 text-left hover:border-teal-200 hover:shadow-xl hover:shadow-teal-500/5 transition-all duration-300"
                                >
                                    <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center text-teal-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </div>
                                    <div className="w-14 h-14 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-slate-500/20">
                                        <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-2">I'm a Healthcare Partner</h3>
                                    <p className="text-sm text-gray-500 leading-relaxed">Register your institution, add clinics and staff, and start offering services on the Uzima platform.</p>
                                </button>
                            </div>

                            {/* Home link */}
                            <div className="text-center">
                                <button
                                    onClick={() => window.location.href = '/search'}
                                    className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-teal-600 transition-colors duration-200"
                                >
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                    </svg>
                                    Back to Home Page
                                </button>
                            </div>

                            {/* Login link */}
                            <div className="text-center mt-8 pt-6 border-t border-gray-100">
                                <p className="text-sm text-gray-500">Already have an account? <Link to="/institution/newlog" className="text-teal-600 font-semibold hover:text-teal-700 transition-colors">Sign in</Link></p>
                            </div>
                        </div>
                    )}

                    {/* ═══════════ PARTNER REGISTRATION ═══════════ */}
                    {mainStep === 'partner' && (
                        <div className="animate-fadeIn">
                            <div className="text-center mb-2">
                                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Partner Registration</h1>
                                <p className="text-gray-500 text-sm">Register your healthcare institution on Uzima</p>
                            </div>

                            <StepIndicator steps={['Institution', 'Facilities', 'Review']} current={step} />

                            {/* Step 0: Institution */}
                            {step === 0 && (
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
                                    <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                                        <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
                                            <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                        </div>
                                        Institution Details
                                    </h3>
                                    <div className="space-y-5">
                                        <div>
                                            <label className={labelClass}>Institution Name <span className="text-red-400">*</span></label>
                                            <input className={inputClass} placeholder="e.g. Nairobi Metropolitan Hospital" value={institution.name || ''} onChange={e => setInstitution({ ...institution, name: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Website <span className="text-red-400">*</span></label>
                                            <input className={inputClass} placeholder="www.example.co.ke" value={institution.website || ''} onChange={e => setInstitution({ ...institution, website: e.target.value })} />
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelClass}>County <span className="text-red-400">*</span></label>
                                                <SearchableSelect
                                                    options={countyOptions}
                                                    value={selectedCounty}
                                                    onChange={value => handleCountySelect(value, () => setInstitution({ ...institution, town: undefined }))}
                                                    placeholder="Select County"
                                                    searchPlaceholder="Search county"
                                                    emptyText="No counties found"
                                                    inputClassName={inputClass}
                                                />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Town <span className="text-red-400">*</span></label>
                                                <SearchableSelect
                                                    options={townOptions}
                                                    value={selectedTown}
                                                    onChange={value => { setSelectedTown(value); setInstitution({ ...institution, town: Number(value) }); }}
                                                    placeholder="Select Town"
                                                    searchPlaceholder="Search town"
                                                    emptyText="No towns found"
                                                    inputClassName={inputClass}
                                                    disabled={!selectedCounty}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className={labelClass}>Email <span className="text-red-400">*</span></label>
                                            <input className={inputClass} placeholder="contact@institution.co.ke" type="email" value={institution.email || ''} onChange={e => setInstitution({ ...institution, email: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Username <span className="text-red-400">*</span></label>
                                            <input className={inputClass} placeholder="Choose a username" value={institution.username || ''} onChange={e => setInstitution({ ...institution, username: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Password <span className="text-red-400">*</span></label>
                                            <PasswordField
                                                placeholder="Create a secure password"
                                                value={institution.password || ''}
                                                onChange={value => setInstitution({ ...institution, password: value })}
                                                visible={!!visiblePasswords['institution-password']}
                                                onToggle={() => togglePasswordVisibility('institution-password')}
                                                inputClassName={inputClass}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                                        <button className={btnOutline} onClick={() => setMainStep('select')}>
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                                            Back
                                        </button>
                                        <button className={canGoToFacilities ? btnPrimary : btnDisabled} disabled={!canGoToFacilities} onClick={() => setStep(1)}>
                                            Continue
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 1: Facilities */}
                            {step === 1 && (
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
                                    <div className="flex justify-between items-center mb-6">
                                        <h3 className="text-lg font-bold text-slate-800">Your Facilities</h3>
                                        <button
                                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-50 text-teal-700 rounded-xl text-sm font-semibold hover:bg-teal-100 transition-colors"
                                            onClick={() => setFacilities([...facilities, { name: '', county: 0, town: 0, category: 0, email: '', password: '', website: 'www.website.org', designation: 1, services: [], users: [] }])}
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                                            Add Facility
                                        </button>
                                    </div>

                                    {facilities.length === 0 ? (
                                        <div className="text-center py-16 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                                            <svg className="w-12 h-12 mx-auto text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                            <p className="text-gray-500 font-medium mb-1">No facilities yet</p>
                                            <p className="text-gray-400 text-sm">Add your first healthcare facility to continue</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-5">
                                            {facilities.map((fac, idx) => (
                                                <div key={idx} className="border border-gray-100 rounded-2xl p-5 bg-gray-50/50 hover:bg-white hover:shadow-sm transition-all">
                                                    <div className="flex justify-between items-center mb-4">
                                                        <h4 className="font-semibold text-slate-800">{fac.name || `Facility ${idx + 1}`}</h4>
                                                        <button className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-all" onClick={() => setFacilities(facilities.filter((_, i) => i !== idx))}>
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                        </button>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                                                        <div><label className={labelClass}>Name <span className="text-red-400">*</span></label><input className={inputClass} placeholder="Facility name" value={fac.name} onChange={e => updateFacility(idx, { name: e.target.value })} /></div>
                                                        <div>
                                                            <label className={labelClass}>Category <span className="text-red-400">*</span></label>
                                                            <select className={selectClass} value={fac.category || ''} onChange={e => updateFacility(idx, { category: Number(e.target.value) })}>
                                                                <option value="">Select Category</option>
                                                                {categories.map((cat: Category) => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
                                                            </select>
                                                        </div>
                                                        <div>
                                                            <label className={labelClass}>County <span className="text-red-400">*</span></label>
                                                            <SearchableSelect
                                                                options={countyOptions}
                                                                value={facilityCounties[idx] || ''}
                                                                onChange={value => handleFacilityCountySelect(idx, value, fac.users)}
                                                                placeholder="Select County"
                                                                searchPlaceholder="Search county"
                                                                emptyText="No counties found"
                                                                inputClassName={inputClass}
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className={labelClass}>Town <span className="text-red-400">*</span></label>
                                                            <SearchableSelect
                                                                options={(facilityTowns[idx] || []).map((town: Town) => ({ value: String(town.town_id), label: town.town }))}
                                                                value={fac.town ? String(fac.town) : ''}
                                                                onChange={value => updateFacility(idx, { town: Number(value), users: fac.users.map(user => ({ ...user, town: Number(value) })) })}
                                                                placeholder="Select Town"
                                                                searchPlaceholder="Search town"
                                                                emptyText="No towns found"
                                                                inputClassName={inputClass}
                                                                disabled={!facilityCounties[idx]}
                                                            />
                                                        </div>
                                                        <div><label className={labelClass}>Email <span className="text-red-400">*</span></label><input className={inputClass} placeholder="facility@example.com" type="email" value={fac.email} onChange={e => updateFacility(idx, { email: e.target.value })} /></div>
                                                        <div><label className={labelClass}>Website</label><input className={inputClass} placeholder="www.facility.com" value={fac.website} onChange={e => updateFacility(idx, { website: e.target.value })} /></div>
                                                        <div><label className={labelClass}>Phone</label><input className={inputClass} placeholder="+254 7XX XXX XXX" value={fac.phone || ''} onChange={e => updateFacility(idx, { phone: e.target.value })} /></div>
                                                        <div className="md:col-span-2"><label className={labelClass}>Password <span className="text-red-400">*</span></label><PasswordField placeholder="Facility password" value={fac.password} onChange={value => updateFacility(idx, { password: value })} visible={!!visiblePasswords[`facility-password-${idx}`]} onToggle={() => togglePasswordVisibility(`facility-password-${idx}`)} inputClassName={inputClass} /></div>
                                                    </div>

                                                    {/* Staff Section */}
                                                    <div className="mt-5 pt-5 border-t border-gray-200">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <h5 className="font-semibold text-sm text-slate-700">Staff Members <span className="text-red-400">*</span></h5>
                                                            <button className="text-teal-600 hover:text-teal-800 text-xs font-semibold flex items-center gap-1" onClick={() => updateFacility(idx, { users: [...fac.users, { fname: '', lname: '', email: '', password: '', title_id: 0, town: fac.town }] })}>
                                                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                                                                Add Staff
                                                            </button>
                                                        </div>
                                                        {fac.users.length === 0 ? (
                                                            <div className="text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                                                <p className="text-gray-400 text-xs">No staff members added</p>
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-3">
                                                                {fac.users.map((user, uidx) => (
                                                                    <div key={uidx} className="p-4 border border-gray-100 rounded-xl bg-white">
                                                                        <div className="flex justify-between items-center mb-3">
                                                                            <span className="text-sm font-medium text-slate-700">{user.fname && user.lname ? `${user.fname} ${user.lname}` : `Staff ${uidx + 1}`}</span>
                                                                            <button className="text-gray-400 hover:text-red-500 transition-colors" onClick={() => updateFacility(idx, { users: fac.users.filter((_, j) => j !== uidx) })}>
                                                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                                                            </button>
                                                                        </div>
                                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                                            <input className={inputClass} placeholder="First Name *" value={user.fname} onChange={e => updateUser(idx, uidx, { fname: e.target.value })} />
                                                                            <input className={inputClass} placeholder="Last Name *" value={user.lname} onChange={e => updateUser(idx, uidx, { lname: e.target.value })} />
                                                                            <input className={inputClass} placeholder="Email *" type="email" value={user.email} onChange={e => updateUser(idx, uidx, { email: e.target.value })} />
                                                                            <select className={selectClass} value={user.title_id || ''} onChange={e => updateUser(idx, uidx, { title_id: Number(e.target.value) })}>
                                                                                <option value="">Select Title *</option>
                                                                                {staffTitles.map(title => (<option key={title.id} value={title.id}>{title.name}</option>))}
                                                                            </select>
                                                                            <div className="md:col-span-2">
                                                                                <PasswordField placeholder="Password *" value={user.password} onChange={value => updateUser(idx, uidx, { password: value })} visible={!!visiblePasswords[`staff-password-${idx}-${uidx}`]} onToggle={() => togglePasswordVisibility(`staff-password-${idx}-${uidx}`)} inputClassName={inputClass} />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                                        <button className={btnOutline} onClick={() => setStep(0)}>Back</button>
                                        <button className={canGoToReview ? btnPrimary : btnDisabled} disabled={!canGoToReview} onClick={() => setStep(2)}>
                                            Review
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Review */}
                            {step === 2 && (
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
                                    <h3 className="text-lg font-bold text-slate-800 mb-1">Review Your Information</h3>
                                    <p className="text-gray-400 text-sm mb-8">Please verify all details before submitting</p>

                                    <div className="space-y-6">
                                        {/* Institution Summary */}
                                        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-gray-100">
                                            <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                                <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                                Institution
                                            </h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div><span className="text-xs text-gray-400 uppercase tracking-wider">Name</span><p className="text-sm font-medium text-slate-800 mt-0.5">{institution.name}</p></div>
                                                <div><span className="text-xs text-gray-400 uppercase tracking-wider">Website</span><p className="text-sm font-medium text-slate-800 mt-0.5">{institution.website}</p></div>
                                                <div><span className="text-xs text-gray-400 uppercase tracking-wider">Town</span><p className="text-sm font-medium text-slate-800 mt-0.5">{towns.find((t: Town) => t.town_id === institution.town)?.town || institution.town}</p></div>
                                                <div><span className="text-xs text-gray-400 uppercase tracking-wider">Email</span><p className="text-sm font-medium text-slate-800 mt-0.5">{institution.email}</p></div>
                                                <div><span className="text-xs text-gray-400 uppercase tracking-wider">Username</span><p className="text-sm font-medium text-slate-800 mt-0.5">{institution.username}</p></div>
                                            </div>
                                        </div>

                                        {/* Facilities Summary */}
                                        <div>
                                            <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                                                <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                                                Facilities ({facilities.length})
                                            </h4>
                                            <div className="space-y-3">
                                                {facilities.map((fac, idx) => (
                                                    <div key={idx} className="border border-gray-100 rounded-xl p-4">
                                                        <div className="flex justify-between items-center mb-3">
                                                            <h5 className="font-medium text-slate-800">{fac.name}</h5>
                                                            <span className="bg-teal-50 text-teal-700 text-xs px-3 py-1 rounded-full font-medium">
                                                                {categories.find((c: Category) => c.id === fac.category)?.name || 'Unknown'}
                                                            </span>
                                                        </div>
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                                                            <div><span className="text-xs text-gray-400">Town</span><p className="text-xs font-medium">{(facilityTowns[idx] || []).find((t: Town) => t.town_id === fac.town)?.town || fac.town}</p></div>
                                                            <div><span className="text-xs text-gray-400">Email</span><p className="text-xs font-medium">{fac.email}</p></div>
                                                            <div><span className="text-xs text-gray-400">Website</span><p className="text-xs font-medium">{fac.website}</p></div>
                                                        </div>
                                                        <div className="pt-3 border-t border-gray-50">
                                                            <span className="text-xs text-gray-400 mb-2 block">Staff ({fac.users.length})</span>
                                                            <div className="flex flex-wrap gap-2">
                                                                {fac.users.map((user, uidx) => (
                                                                    <span key={uidx} className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full">
                                                                        {user.fname} {user.lname} &middot; {staffTitles.find((t: StaffTitle) => t.id === user.title_id)?.name || ''}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-8 pt-6 border-t border-gray-100">
                                        <p className="text-xs text-gray-400 mb-4">By submitting, you agree to our Terms of Service and Privacy Policy.</p>
                                        <div className="flex justify-between">
                                            <button className={btnOutline} onClick={() => setStep(1)}>Back</button>
                                            <button className={btnPrimary} onClick={RegisterPartner}>
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                                Submit Registration
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ═══════════ PATIENT REGISTRATION ═══════════ */}
                    {mainStep === 'patient' && (
                        <div className="animate-fadeIn">
                            <div className="text-center mb-2">
                                <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">Patient Registration</h1>
                                <p className="text-gray-500 text-sm">Create your personal Uzima health account</p>
                            </div>

                            <StepIndicator steps={['Personal Info', 'Health', 'Review']} current={subStep} />

                            {/* Sub-step 0: Personal Info */}
                            {subStep === 0 && (
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
                                    <h3 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2">
                                        <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
                                            <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        </div>
                                        Personal Information
                                    </h3>
                                    <div className="space-y-5">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelClass}>First Name <span className="text-red-400">*</span></label>
                                                <input className={inputClass} placeholder="Enter your first name" value={firstName} onChange={e => setFirstName(e.target.value)} />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Last Name <span className="text-red-400">*</span></label>
                                                <input className={inputClass} placeholder="Enter your last name" value={lastName} onChange={e => setLastName(e.target.value)} />
                                            </div>
                                        </div>

                                        <div>
                                            <label className={labelClass}>Email Address <span className="text-red-400">*</span></label>
                                            <input className={inputClass} placeholder="you@example.com" type="email" value={email} onChange={e => setEmail(e.target.value)} />
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelClass}>Password <span className="text-red-400">*</span></label>
                                                <PasswordField placeholder="Create a secure password" value={password} onChange={setPassword} visible={!!visiblePasswords['patient-password']} onToggle={() => togglePasswordVisibility('patient-password')} inputClassName={inputClass} />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Confirm Password <span className="text-red-400">*</span></label>
                                                <PasswordField className={`${inputClass} ${cPass && password !== cPass ? 'ring-2 ring-red-300 border-red-300' : ''}`} placeholder="Confirm your password" value={cPass} onChange={setCPass} visible={!!visiblePasswords['patient-confirm-password']} onToggle={() => togglePasswordVisibility('patient-confirm-password')} inputClassName={inputClass} />
                                                {cPass && password !== cPass && <p className="text-xs text-red-500 mt-1">Passwords do not match</p>}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className={labelClass}>County <span className="text-red-400">*</span></label>
                                                <SearchableSelect
                                                    options={countyOptions}
                                                    value={selectedCounty}
                                                    onChange={value => handleCountySelect(value)}
                                                    placeholder="Select County"
                                                    searchPlaceholder="Search county"
                                                    emptyText="No counties found"
                                                    inputClassName={inputClass}
                                                />
                                            </div>
                                            <div>
                                                <label className={labelClass}>Town <span className="text-red-400">*</span></label>
                                                <SearchableSelect
                                                    options={townOptions}
                                                    value={selectedTown}
                                                    onChange={value => setSelectedTown(value)}
                                                    placeholder="Select Town"
                                                    searchPlaceholder="Search town"
                                                    emptyText="No towns found"
                                                    inputClassName={inputClass}
                                                    disabled={!selectedCounty}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                                        <button className={btnOutline} onClick={() => setMainStep('select')}>
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                                            Back
                                        </button>
                                        <button
                                            className={firstName && lastName && email && password && cPass && password === cPass && selectedCounty && selectedTown ? btnPrimary : btnDisabled}
                                            disabled={!firstName || !lastName || !email || !password || !cPass || password !== cPass || !selectedCounty || !selectedTown}
                                            onClick={() => setSubStep(1)}
                                        >
                                            Continue
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Sub-step 1: Health Interests */}
                            {subStep === 1 && (
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
                                    <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                                        <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
                                            <svg className="w-4 h-4 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                                        </div>
                                        Health Interests
                                    </h3>
                                    <p className="text-gray-400 text-sm mb-6">Select the healthcare services you're interested in</p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                                        {services.map((service: any) => (
                                            <label
                                                key={service.id}
                                                className={`flex items-center gap-3 p-3.5 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                                                    selectedInterests.includes(service.service_id)
                                                        ? 'border-teal-500 bg-teal-50/50 text-teal-800'
                                                        : 'border-gray-100 bg-white hover:border-gray-200 text-gray-700'
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 accent-teal-600"
                                                    checked={selectedInterests.includes(service.service_id)}
                                                    onChange={() => {
                                                        if (selectedInterests.includes(service.service_id)) {
                                                            setSelectedInterests(selectedInterests.filter(id => id !== service.service_id));
                                                        } else {
                                                            setSelectedInterests([...selectedInterests, service.service_id]);
                                                        }
                                                    }}
                                                />
                                                <span className="text-sm font-medium">{service.service_name}</span>
                                            </label>
                                        ))}
                                    </div>

                                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="w-4 h-4 mt-0.5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 accent-teal-600"
                                                checked={consent}
                                                onChange={() => setConsent(!consent)}
                                            />
                                            <span className="text-sm text-gray-600 leading-relaxed">
                                                I consent to Uzima processing my health information for the purpose of connecting me with relevant healthcare providers.
                                            </span>
                                        </label>
                                    </div>

                                    <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                                        <button className={btnOutline} onClick={() => setSubStep(0)}>Back</button>
                                        <button
                                            className={selectedInterests.length > 0 && consent ? btnPrimary : btnDisabled}
                                            disabled={selectedInterests.length === 0 || !consent}
                                            onClick={() => setSubStep(2)}
                                        >
                                            Continue
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Sub-step 2: Review */}
                            {subStep === 2 && (
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
                                    <h3 className="text-lg font-bold text-slate-800 mb-1">Review Your Information</h3>
                                    <p className="text-gray-400 text-sm mb-8">Please verify all details before submitting</p>

                                    <div className="space-y-6">
                                        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-gray-100">
                                            <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                                <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                                Personal Information
                                            </h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div><span className="text-xs text-gray-400 uppercase tracking-wider">Full Name</span><p className="text-sm font-medium text-slate-800 mt-0.5">{firstName} {lastName}</p></div>
                                                <div><span className="text-xs text-gray-400 uppercase tracking-wider">Email</span><p className="text-sm font-medium text-slate-800 mt-0.5">{email}</p></div>
                                                <div><span className="text-xs text-gray-400 uppercase tracking-wider">County</span><p className="text-sm font-medium text-slate-800 mt-0.5">{counties.find((c: County) => c.county_code.toString() === selectedCounty)?.county || selectedCounty}</p></div>
                                                <div><span className="text-xs text-gray-400 uppercase tracking-wider">Town</span><p className="text-sm font-medium text-slate-800 mt-0.5">{towns.find((t: Town) => t.town_id.toString() === selectedTown)?.town || selectedTown}</p></div>
                                            </div>
                                        </div>

                                        <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-gray-100">
                                            <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                                                <svg className="w-5 h-5 text-teal-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" /></svg>
                                                Health Interests
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedInterests.map((interestId) => {
                                                    const service = services.find((s: any) => s.service_id === interestId);
                                                    return service ? (
                                                        <span key={interestId} className="bg-teal-50 text-teal-700 text-xs font-medium px-3 py-1.5 rounded-full border border-teal-100">
                                                            {service.service_name}
                                                        </span>
                                                    ) : null;
                                                })}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-8 pt-6 border-t border-gray-100">
                                        <p className="text-xs text-gray-400 mb-4">By submitting, you agree to our Terms of Service and Privacy Policy.</p>
                                        <div className="flex justify-between">
                                            <button className={btnOutline} onClick={() => setSubStep(1)}>Back</button>
                                            <button className={btnPrimary} onClick={RegisterUser}>
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                                Submit Registration
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Inline animation styles */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(12px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.4s ease-out;
                }
            `}</style>
        </div>
    );
}

export default NewRegister;
